import React, { useEffect } from 'react';
import './ErrorNotification.css';

type ErrorType = 'error' | 'warning' | 'info';

interface ErrorNotificationProps {
  error: string | null;
  onDismiss: () => void;
  autoDismissMs?: number;
  type?: ErrorType;
}

/**
 * ErrorNotification Component
 *
 * Displays user-friendly error messages with auto-dismiss
 */
const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  error,
  onDismiss,
  autoDismissMs = 5000,
  type = 'error'
}) => {
  useEffect(() => {
    if (error && autoDismissMs > 0) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoDismissMs);

      return () => clearTimeout(timer);
    }
  }, [error, autoDismissMs, onDismiss]);

  if (!error) return null;

  const getIcon = (): string => {
    switch (type) {
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      case 'error':
      default:
        return '❌';
    }
  };

  const getUserFriendlyMessage = (errorMsg: string): string => {
    // Map technical errors to user-friendly messages
    const errorMappings: Record<string, string> = {
      'Failed to create idea': '创建闪念失败，请稍后重试',
      'Failed to update idea': '更新闪念失败，请稍后重试',
      'Failed to delete idea': '删除闪念失败，请稍后重试',
      'Failed to load ideas': '加载闪念列表失败，请刷新页面',
      'Network error': '网络连接失败，请检查网络设置',
      'Unauthorized': '登录已过期，请重新登录',
      'Invalid response format': '服务器响应格式错误，请联系管理员',
      'Idea not found': '找不到该闪念'
    };

    // Check for exact match
    if (errorMappings[errorMsg]) {
      return errorMappings[errorMsg];
    }

    // Check for partial match
    for (const [key, value] of Object.entries(errorMappings)) {
      if (errorMsg.includes(key)) {
        return value;
      }
    }

    // Return original message if no mapping found
    return errorMsg || '操作失败，请稍后重试';
  };

  return (
    <div className={`error-notification error-notification-${type}`}>
      <div className="error-notification-content">
        <span className="error-notification-icon">{getIcon()}</span>
        <span className="error-notification-message">
          {getUserFriendlyMessage(error)}
        </span>
      </div>
      <button
        className="error-notification-dismiss"
        onClick={onDismiss}
        aria-label="关闭"
      >
        ×
      </button>
    </div>
  );
};

export default ErrorNotification;
